import React, { useContext } from "react";
import MUIDataTable from "mui-datatables";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import { ThemeContext } from "../Context/Theme";

const getMuiTheme = (dark) =>
  createMuiTheme({
    palette: {
      action: {
        disabled: dark ? "#27343f" : "#c0cdd8",
      },
    },
    overrides: {
      MUIDataTable: {
        root: {},
        paper: {
          boxShadow: "none",
          backgroundColor: dark ? "#212121" : "#f2f2f2",
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },
      MuiTextField: {
        root: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },

      MuiCheckbox: {
        root: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },

      MuiIconButton: {
        root: {
          color: dark ? "#c0cdd8" : "#445B6E",
          "&:hover": {
            color: dark ? "#c0cdd8" : "#445B6E",
          },
        },
      },

      MuiIcon: {
        root: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },
      MUIDataTableToolbarSelect: {
        root: {
          backgroundColor: dark ? "#212121" : "#f2f2f2",
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },

      MUIDataTableToolbar: {
        titleText: {
          color: dark ? "#c0cdd8" : "#445B6E",
          fontWeight: "bolder",
          fontSize: 24,
        },
      },
      MUIDataTableSearch: {
        searchIcon: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },

      MUIDataTableFilter: {
        root: {
          backgroundColor: dark ? "#333333" : "#f2f2f2",
          color: dark ? "#c0cdd8" : "#445B6E",
          borderRadius: 10,
        },
        title: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },

      MUIDataTableViewCol: {
        root: {
          backgroundColor: dark ? "#333333" : "#f2f2f2",
          color: dark ? "#c0cdd8" : "#445B6E",
        },
        title: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
        label: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },

      MuiInputBase: {
        root: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
        input: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },
      MuiInputLabel: {
        root: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },

      MuiMenu: {
        paper: {
          backgroundColor: dark ? "#212121" : "#f2f2f2",
        },
        list: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },

      MUIDataTableHeadCell: {
        fixedHeader: {
          "z-index": "0 !important",
          backgroundColor: dark ? "#212121" : "#f2f2f2",
          color: dark ? "#c0cdd8" : "#445B6E",
          fontSize: 18,
          fontWeight: "bolder",
          textAlign: "center",
        },
        data: {
          color: dark ? "#c0cdd8" : "#445B6E",
          fontSize: 18,
          fontWeight: "bolder",
        },
        contentWrapper: {
          justifyContent: "center",
        },
        sortActive: {
          color: dark ? "#c0cdd8" : "#445B6E",
          fontSize: 18,
          fontWeight: "bolder",
        },
        sortAction: {
          alignItems: "center",
        },
      },

      MUIDataTableBodyCell: {
        root: {
          color: dark ? "#c0cdd8" : "#445B6E",
          textAlign: "center",
        },
      },
      MuiToolbar: {
        root: {
          backgroundColor: dark ? "#212121" : "#f2f2f2",
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },
      MUIDataTableSelectCell: {
        fixedHeader: {
          "z-index": "0 !important",
        },
        fixedLeft: {
          "z-index": "0 !important",
        },
        headerCell: {
          "z-index": "0 !important",
          backgroundColor: dark ? "#212121" : "#f2f2f2",
        },
      },
      MuiTablePagination: {
        selectIcon: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
        caption: {
          color: dark ? "#c0cdd8" : "#445B6E",
        },
      },
    },
  });

const Table = ({ data, columns, title, onRowsDelete, onTableChange }) => {
  const { dark } = useContext(ThemeContext);

  const theme = getMuiTheme(dark);
  const customSortHandler = (arr, colIndex, sortOrder) => {
    return arr.sort((a, b) => {
      // if number
      if (!isNaN(a.data[colIndex])) {
        return sortOrder === "asc"
          ? Number(a.data[colIndex]) > Number(b.data[colIndex])
            ? -1
            : 1
          : Number(a.data[colIndex]) > Number(b.data[colIndex])
          ? 1
          : -1;
      }
      // if date
      else if (new Date(a.data[colIndex]) != "Invalid Date") {
        return sortOrder === "asc"
          ? Date.parse(a.data[colIndex]) > Date.parse(b.data[colIndex])
            ? -1
            : 1
          : Date.parse(a.data[colIndex]) > Date.parse(b.data[colIndex])
          ? 1
          : -1;
      }
      // if string
      else if (typeof a.data[colIndex] === "string") {
        return sortOrder === "asc"
          ? a.data[colIndex].trim().length > b.data[colIndex].trim().length
            ? -1
            : 1
          : a.data[colIndex].trim().length > b.data[colIndex].trim().length
          ? 1
          : -1;
      }
    });
  };

  const options = {
    filter: true,
    filterType: "textField",
    print: false,
    download: false,
    draggableColumns: {
      enabled: true,
    },
    pagination: true,
    rowsPerPageOptions: [5, 10, 20, 30],
    search: true,
    caseSensitive: false,
    onRowsDelete: onRowsDelete,
    selectableRowsHeader: false,
    selectableRows: false,
    searchOpen: true,
    searchAlwaysOpen: true,
    // viewColumns: true,
    onTableChange: onTableChange,
    customSort: customSortHandler,
  };

  return (
    <ThemeProvider theme={theme}>
      <MUIDataTable
        title={title}
        data={data}
        columns={columns}
        options={options}
      />
    </ThemeProvider>
  );
};

export default Table;
